import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";
import type {
  ShiftFormManagerDashboard,
  ShiftFormManagerDashboardFilters,
  ShiftProductionForm,
  ShiftProductionFormListItem,
} from "../../../shared/shift-production-form/contracts";
import {
  decideShiftForm,
  getShiftForm,
  getShiftFormManagerDashboard,
  getShiftFormList,
} from "../shift-production-form/shift-production-form-gateway";

type Phase = "loading" | "ready" | "error";

const istanbulDate = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Istanbul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

export function useProductionManagerDashboard() {
  const [filters, setFilterState] = useState<ShiftFormManagerDashboardFilters>(() => {
    const date = istanbulDate();
    return { dateFrom: date, dateTo: date };
  });
  const [dashboard, setDashboard] = useState<ShiftFormManagerDashboard | null>(null);
  const [accessibleLineNames, setAccessibleLineNames] = useState<string[]>([]);
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<string | null>(null);
  const [selectedForm, setSelectedForm] = useState<ShiftProductionForm | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [accessibleForms, setAccessibleForms] = useState<ShiftProductionFormListItem[]>([]);
  const [formsPhase, setFormsPhase] = useState<Phase>("ready");
  const [formsError, setFormsError] = useState<string | null>(null);
  const [formsOffset, setFormsOffset] = useState(0);
  const [formsTotal, setFormsTotal] = useState(0);
  const dashboardRequest = useRef(0);
  const detailRequest = useRef(0);
  const formsRequest = useRef(0);
  const decisionRequest = useRef(0);

  const setFilters = useCallback((next: SetStateAction<ShiftFormManagerDashboardFilters>) => {
    setFormsOffset(0);
    setFilterState(next);
  }, []);

  const reload = useCallback(async () => {
    const request = ++dashboardRequest.current;
    setPhase("loading");
    setError(null);
    setDashboard(null);
    try {
      const next = await getShiftFormManagerDashboard(filters);
      if (request !== dashboardRequest.current) return;
      setDashboard(next);
      setAccessibleLineNames(next.accessibleLineNames);
      setPhase("ready");
    } catch (reason) {
      if (request !== dashboardRequest.current) return;
      setError(reason instanceof Error ? reason.message : "Üretim takip verileri alınamadı.");
      setPhase("error");
    }
  }, [filters]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => () => {
    dashboardRequest.current += 1;
    detailRequest.current += 1;
    formsRequest.current += 1;
    decisionRequest.current += 1;
  }, []);

  const openForm = async (formId: string) => {
    const request = ++detailRequest.current;
    setDetailBusy(true);
    setError(null);
    try {
      const response = await getShiftForm(formId);
      if (request !== detailRequest.current) return;
      setSelectedForm(response.form);
    } catch (reason) {
      if (request !== detailRequest.current) return;
      setError(reason instanceof Error ? reason.message : "Form ayrıntısı açılamadı.");
    } finally {
      if (request === detailRequest.current) setDetailBusy(false);
    }
  };

  const reloadAccessibleForms = useCallback(async () => {
    const request = ++formsRequest.current;
    setFormsPhase("loading");
    setFormsError(null);
    try {
      const page = await getShiftFormList("accessible", {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        departmentName: filters.departmentName,
        lineName: filters.lineName,
        shift: filters.shift,
        limit: 100,
        offset: formsOffset,
      });
      if (request !== formsRequest.current) return;
      setAccessibleForms(page.items);
      setFormsTotal(page.total);
      setFormsPhase("ready");
    } catch (reason) {
      if (request !== formsRequest.current) return;
      setFormsError(reason instanceof Error ? reason.message : "Form listesi açılamadı.");
      setFormsPhase("error");
    }
  }, [filters, formsOffset]);

  const decide = async (
    action: "APPROVE",
    explanation: string,
    missingMeasurementAcceptanceReason: string,
  ) => {
    if (!selectedForm) return;
    const request = ++decisionRequest.current;
    const formId = selectedForm.id;
    setDetailBusy(true);
    setError(null);
    try {
      const response = await decideShiftForm(
        formId,
        selectedForm.version,
        selectedForm.revisionNumber,
        action,
        explanation,
        missingMeasurementAcceptanceReason,
      );
      if (request !== decisionRequest.current) return;
      setSelectedForm(response.form);
      await reload();
      await reloadAccessibleForms();
    } catch (reason) {
      if (request !== decisionRequest.current) return;
      setError(reason instanceof Error ? reason.message : "Onay kararı kaydedilemedi.");
    } finally {
      if (request === decisionRequest.current) setDetailBusy(false);
    }
  };

  const closeForm = useCallback(() => {
    detailRequest.current += 1;
    decisionRequest.current += 1;
    setDetailBusy(false);
    setSelectedForm(null);
  }, []);

  return {
    filters,
    setFilters,
    dashboard,
    accessibleLineNames,
    phase,
    error,
    reload,
    selectedForm,
    detailBusy,
    accessibleForms,
    formsPhase,
    formsError,
    formsOffset,
    formsTotal,
    setFormsOffset,
    reloadAccessibleForms,
    openForm,
    decide,
    closeForm,
  };
}
